import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Chip,
    Button,
    Paper,
    IconButton,
    CircularProgress,
    Alert,
    Tooltip,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Stack,
    Avatar,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    alpha,
    Divider,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Edit as EditIcon,
    Description as DescriptionIcon,
    PriorityHigh as PriorityHighIcon,
    History as HistoryIcon,
    SyncAlt as SyncAltIcon,
    CheckCircle as CheckCircleIcon,
    Schedule as ScheduleIcon,
    Notes as NotesIcon,
    NotificationsActive as NotificationsActiveIcon,
    Pending as PendingIcon,
} from '@mui/icons-material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { format, isAfter } from 'date-fns';
import Brandcrumb from '../../../../componentsUiBlock/Brandcrumb';
import DeleteConfirmDialog from '../../../../componentsUiBlock/DeleteConfirmDialog';
import { request } from '../../../../utils/request';
import { toast } from 'react-toastify';
import { Priority, Reminder, Status } from '../../types';

// Helper functions
const getPriorityColor = (priority?: Priority) => {
    switch (priority) {
        case 'HIGH': return 'error';
        case 'MEDIUM': return 'warning';
        case 'LOW': return 'success';
        default: return 'default';
    }
};

const getPriorityIcon = (priority?: Priority) => {
    switch (priority) {
        case 'HIGH': return <PriorityHighIcon color="error" fontSize="small" />;
        case 'MEDIUM': return <PriorityHighIcon color="warning" fontSize="small" />;
        case 'LOW': return <PriorityHighIcon color="success" fontSize="small" />;
        default: return <PriorityHighIcon color="disabled" fontSize="small" />;
    }
};

const getStatusColor = (status?: Status) => {
    switch (status) {
        case 'PENDING': return 'warning';
        case 'CLOSED': return 'success';
        default: return 'default';
    }
};

const getStatusIcon = (status?: Status) => {
    switch (status) {
        case 'PENDING': return <PendingIcon fontSize="small" />;
        case 'CLOSED': return <CheckCircleIcon fontSize="small" />;
        default: return <ScheduleIcon fontSize="small" />;
    }
};

const getStatusIconBall = (status?: Status) => {
    switch (status) {
        case 'PENDING': return '⏳';
        case 'CLOSED': return '✅';
        default: return '⚪';
    }
};

const formatDateTime = (date: any) => {
    if (!date) return 'N/A';
    try {
        return format(new Date(date), 'dd MMM yyyy, HH:mm');
    } catch {
        return 'Invalid date';
    }
};

const formatDate = (date: any) => {
    if (!date) return 'N/A';
    try {
        return format(new Date(date), 'dd/MM/yyyy');
    } catch {
        return 'Invalid date';
    }
};

const isOverdue = (reminderDate: Date | string) => {
    return isAfter(new Date(), new Date(reminderDate));
};

const ShowReminder: React.FC = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [reminder, setReminder] = useState<Reminder | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [statusHistory, setStatusHistory] = useState<any[]>([]);

    // Status change dialog
    const [statusDialogOpen, setStatusDialogOpen] = useState(false);
    const [newStatus, setNewStatus] = useState<string>('');
    const [statusNote, setStatusNote] = useState<string>('');
    const [changingStatus, setChangingStatus] = useState(false);

    // Delete dialog
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const fetchReminder = useCallback(async () => {
        if (!id) return;

        setLoading(true);
        try {
            const response = await request({
                url: `reminder/get-single-reminder/${id}`,
                method: 'get',
            });

            if (response.success && response.data) {
                const reminderData = response.data;
                setReminder(reminderData);

                // Set status history
                let history = reminderData.status_history || [];
                if (history.length === 0 && reminderData.status) {
                    history = [{
                        from_status: undefined,
                        to_status: reminderData.status,
                        note: reminderData.statusNote || '',
                        changed_at: reminderData.createdAt || new Date(),
                        is_initial: true,
                    }];
                }
                setStatusHistory(history);
            } else {
                setError('Reminder not found');
            }
        } catch (err: any) {
            console.error('Error fetching reminder:', err);
            setError(err.message || 'Failed to load reminder details');
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchReminder();
    }, [fetchReminder]);

    const handleStatusChange = async () => {
        if (!newStatus || !reminder || newStatus === reminder.status) return;

        setChangingStatus(true);
        try {
            const payload: any = {
                status: newStatus,
                statusNote: statusNote.trim()
            };

            const res = await request({
                url: `reminder/update-reminder/${id}`,
                method: 'put',
                data: payload,
            });

            if (res.success) {
                toast.success('Status updated successfully!');
                setStatusDialogOpen(false);
                setNewStatus('');
                setStatusNote('');
                fetchReminder();
            } else {
                toast.error(res.message || 'Failed to update status');
            }
        } catch (err: any) {
            console.error('Status change error:', err);
            toast.error(err?.message || 'Failed to update status');
        } finally {
            setChangingStatus(false);
        }
    };

    const openStatusDialog = () => {
        if (reminder) {
            setNewStatus(reminder.status ?? '');
            setStatusNote('');
            setStatusDialogOpen(true);
        }
    };

    const handleEdit = () => {
        navigate(`/reminder/editReminder/${id}`);
    };

    const handleDelete = async () => {
        if (!reminder) return;

        try {
            setDeleting(true);
            const response = await request({
                url: `reminder/delete-reminder/${reminder._id}`,
                method: 'delete',
            });

            if (response.success) {
                toast.success(response.message || 'Reminder deleted successfully');
                navigate('/reminder');
            } else {
                toast.error(response.message || 'Failed to delete reminder');
            }
        } catch (error: any) {
            console.error('Error deleting reminder:', error);
            toast.error(error?.message || 'Failed to delete reminder');
        } finally {
            setDeleting(false);
            setDeleteDialogOpen(false);
        }
    };

    // Get activity timeline
    const getActivityTimeline = () => {
        const timeline = [...statusHistory];
        timeline.sort((a, b) => {
            const dateA = new Date(a.changed_at || 0);
            const dateB = new Date(b.changed_at || 0);
            return dateB.getTime() - dateA.getTime();
        });
        return timeline;
    };

    if (loading) {
        return (
            <>
                <Box sx={{ mb: 2 }}>
                    <Brandcrumb />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                    <CircularProgress />
                    <Typography variant="body2" sx={{ ml: 2, color: 'text.secondary' }}>
                        Loading reminder details...
                    </Typography>
                </Box>
            </>
        );
    }

    if (error || !reminder) {
        return (
            <>
                <Box sx={{ mb: 2 }}>
                    <Brandcrumb />
                </Box>
                <Card>
                    <CardContent>
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error || 'Reminder not found'}
                        </Alert>
                        <Button
                            variant="outlined"
                            startIcon={<ArrowBackIcon />}
                            onClick={() => navigate('/reminder')}
                            size="small"
                        >
                            Back to Reminders
                        </Button>
                    </CardContent>
                </Card>
            </>
        );
    }

    const isOverdueReminder = isOverdue(reminder.reminderDate) && reminder.status !== 'CLOSED';
    const activityTimeline = getActivityTimeline();

    return (
        <>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'start', alignItems: 'center' }}>
                <Brandcrumb />
                <Chip
                    onClick={() => navigate(-1)}
                    icon={<ArrowBackIosNewIcon sx={{ fontSize: "14px" }} />}
                    label="Back"
                    variant="filled"
                    color="secondary"
                    sx={{
                        fontSize: '14px',
                        color: 'text.secondary',
                        cursor: 'pointer',
                        '&:hover': {
                            backgroundColor: 'action.hover'
                        }
                    }}
                />
            </Box>

            <Box sx={{ mb: 3 }}>
                {/* Header */}
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2,
                    p: 2,
                    backgroundColor: 'white',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <IconButton
                            onClick={() => navigate('/reminder')}
                            size="small"
                            sx={{ color: 'text.primary' }}
                        >
                            <ArrowBackIcon />
                        </IconButton>
                        <Box>
                            <Typography variant="h6" fontWeight="bold">
                                Reminder Details
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Edit Reminder" placement='top' arrow>
                            <IconButton
                                size="small"
                                color="primary"
                                onClick={handleEdit}
                            >
                                <EditIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        
                    </Box>
                </Box>

                {/* Two Column Layout */}
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 2 }}>

                    {/* LEFT COLUMN */}
                    <Box sx={{ flex: 1 }}>
                        {/* Status Card */}
                        <Box sx={{ display: 'flex', mb: 1, gap: 1 }}>
                            <Card elevation={0} sx={{
                                flex: 1,
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'divider',
                                p: 2,
                                py: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between'
                            }}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    {getStatusIcon(reminder.status)}
                                    <Box sx={{ ml: 1.5 }}>
                                        <Stack direction={'row'} spacing={6}>
                                            <Typography variant="caption" color="text.secondary" display="block">
                                                Current Status
                                            </Typography>
                                        </Stack>
                                        <Stack direction={'row'} spacing={0.5} alignItems={"center"}>
                                            <Chip
                                                label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <span>{getStatusIconBall(reminder.status)}</span>
                                                    <Typography variant="caption" sx={{ fontWeight: 500 }}>
                                                        {reminder.status}
                                                    </Typography>
                                                </Box>}
                                                color={getStatusColor(reminder.status) as any}
                                                size="small"
                                                sx={{ height: 22, fontSize: '0.75rem', mt: 0.5 }}
                                            />
                                            {reminder.status !== 'CLOSED' && (
                                                <Tooltip title="Update Status" arrow placement='top'>
                                                    <IconButton size="small" color="primary" onClick={openStatusDialog}>
                                                        <SyncAltIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </Stack>
                                    </Box>
                                </Box>
                                {isOverdueReminder && (
                                    <Chip
                                        label="Overdue"
                                        size="small"
                                        color="error"
                                        sx={{ fontSize: '0.7rem', height: 24 }}
                                    />
                                )}
                            </Card>
                        </Box>

                        {/* Priority Card */}
                        <Card elevation={0} sx={{
                            borderRadius: 1,
                            mb: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                            display: 'flex',
                            alignItems: 'center',
                            p: 2,
                            py: 1
                        }}>
                            {getPriorityIcon(reminder.priority)}
                            <Box sx={{ ml: 1.5 }}>
                                <Typography variant="caption" color="text.secondary" display="block">
                                    Priority
                                </Typography>
                                <Typography variant="body2" fontWeight="medium">
                                    {reminder.priority}
                                </Typography>
                            </Box>
                        </Card>

                        {/* Description Section */}
                        <Card elevation={0} sx={{
                            borderRadius: 1,
                            mb: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                            p: 2
                        }}>
                            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <DescriptionIcon fontSize="small" />
                                Description
                            </Typography>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-line', fontSize: '0.9rem' }}>
                                {reminder.description}
                            </Typography>
                        </Card>

                        {/* Status Note */}
                        {reminder.statusNote && (
                            <Card elevation={0} sx={{
                                borderRadius: 1,
                                mb: 1,
                                border: '1px solid',
                                borderColor: 'divider',
                                p: 2,
                                bgcolor: alpha(reminder.status === 'CLOSED' ? '#4caf50' : '#ff9800', 0.08)
                            }}>
                                <Typography variant="subtitle2" fontWeight="bold" color={reminder.status === 'CLOSED' ? 'success.main' : 'warning.main'} sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <NotesIcon fontSize="small" />
                                    Status Note
                                </Typography>
                                <Typography variant="body2" sx={{ fontSize: '0.9rem' }}>
                                    {reminder.statusNote}
                                </Typography>
                            </Card>
                        )}

                        {/* Activity Timeline */}
                        <Card elevation={0} sx={{
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                            p: 2
                        }}>
                            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <HistoryIcon fontSize="small" />
                                Activity Timeline
                            </Typography>

                            {activityTimeline.length > 0 ? (
                                <List sx={{
                                    position: 'relative',
                                    '&:before': {
                                        content: '""',
                                        position: 'absolute',
                                        left: 12,
                                        top: 5,
                                        bottom: 0,
                                        width: 2,
                                        backgroundColor: 'divider'
                                    },
                                    py: 0
                                }}>
                                    {activityTimeline.map((activity, index) => (
                                        <ListItem
                                            key={index}
                                            sx={{
                                                position: 'relative',
                                                alignItems: 'flex-start',
                                                py: 1.5,
                                                px: 0,
                                                '&:last-child': {
                                                    pb: 0
                                                }
                                            }}
                                        >
                                            <ListItemAvatar sx={{ minWidth: 36 }}>
                                                <Avatar sx={{
                                                    width: 24,
                                                    height: 24,
                                                    bgcolor: getStatusColor(activity.to_status) + '.main',
                                                    color: 'white',
                                                    fontSize: '0.85rem'
                                                }}>
                                                    {getStatusIcon(activity.to_status)}
                                                </Avatar>
                                            </ListItemAvatar>
                                            <ListItemText
                                                sx={{ m: 0 }}
                                                primary={
                                                    <Box>
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                                                            <Box>
                                                                {activity.from_status ? (
                                                                    <Typography variant="body2" fontWeight="medium" sx={{ fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                        <span>{getStatusIconBall(activity.from_status)}</span> {activity.from_status} → <span>{getStatusIconBall(activity.to_status)}</span> {activity.to_status}
                                                                    </Typography>
                                                                ) : (
                                                                    <Typography variant="body2" fontWeight="medium" sx={{ fontSize: '0.85rem' }}>
                                                                        Reminder created as {getStatusIconBall(activity.to_status)} {activity.to_status}
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                                                {formatDateTime(activity.changed_at)}
                                                            </Typography>
                                                        </Box>
                                                        {activity.note && (
                                                            <Paper elevation={0} sx={{
                                                                py: 0,
                                                                px: 1,
                                                                mt: 1,
                                                                bgcolor: 'background.default',
                                                                border: '1px solid',
                                                                borderColor: 'divider',
                                                                borderRadius: 0.5
                                                            }}>
                                                                <Typography variant="caption" sx={{ fontSize: '0.8rem' }}>
                                                                    {activity.note}
                                                                </Typography>
                                                            </Paper>
                                                        )}
                                                    </Box>
                                                }
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            ) : (
                                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                    No activity history available
                                </Typography>
                            )}
                        </Card>
                    </Box>

                    {/* RIGHT COLUMN - Additional Information */}
                    <Box sx={{ flex: 1 }}>
                        {/* Summary Card */}
                        <Card elevation={0} sx={{
                            borderRadius: 1,
                            mb: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                            p: 2
                        }}>
                            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <NotificationsActiveIcon fontSize="small" />
                                Reminder Summary
                            </Typography>
                            <Stack spacing={2}>
                                <Box>
                                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                        Description
                                    </Typography>
                                    <Typography variant="body2" fontWeight="medium" sx={{ wordBreak: 'break-word' }}>
                                        {reminder.description}
                                    </Typography>
                                </Box>

                                <Divider />

                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="caption" color="text.secondary">Event Date</Typography>
                                    <Typography variant="body2" fontWeight="medium">
                                        {formatDate(reminder.date)}
                                    </Typography>
                                </Box>

                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="caption" color="text.secondary">Reminder Date & Time</Typography>
                                    <Typography variant="body2" fontWeight="medium" color={isOverdueReminder ? 'error.main' : 'text.primary'}>
                                        {formatDateTime(reminder.reminderDate)}
                                    </Typography>
                                </Box>

                                {reminder.nextReminderDate && (
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="caption" color="text.secondary">Next Reminder</Typography>
                                        <Typography variant="body2" fontWeight="medium">
                                            {formatDateTime(reminder.nextReminderDate)}
                                        </Typography>
                                    </Box>
                                )}

                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="caption" color="text.secondary">Priority</Typography>
                                    <Chip
                                        label={reminder.priority}
                                        size="small"
                                        color={getPriorityColor(reminder.priority)}
                                        sx={{ height: 20, fontSize: '0.7rem' }}
                                    />
                                </Box>

                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="caption" color="text.secondary">Status</Typography>
                                    <Chip
                                        label={reminder.status}
                                        size="small"
                                        color={getStatusColor(reminder.status)}
                                        sx={{ height: 20, fontSize: '0.7rem' }}
                                    />
                                </Box>
                            </Stack>
                        </Card>
    
                    </Box>
                </Box>
            </Box>

            {/* Status Change Dialog */}
            <Dialog open={statusDialogOpen} onClose={() => setStatusDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
                    <SyncAltIcon fontSize="small" />
                    <Typography variant="subtitle1" fontWeight="bold">
                        Update Status
                    </Typography>
                </DialogTitle>
                <DialogContent sx={{ py: 2 }}>
                    <Stack spacing={2}>
                        <Box>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                Current Status
                            </Typography>
                            <Chip
                                label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <span>{getStatusIconBall(reminder?.status)}</span>
                                    <Typography variant="caption" sx={{ fontWeight: 500 }}>
                                        {reminder?.status}
                                    </Typography>
                                </Box>}
                                color={getStatusColor(reminder?.status) as any}
                                size="small"
                                sx={{ fontSize: '0.75rem' }}
                            />
                        </Box>

                        <FormControl fullWidth size="small">
                            <InputLabel>New Status *</InputLabel>
                            <Select
                                value={newStatus}
                                label="New Status *"
                                onChange={(e) => setNewStatus(e.target.value)}
                            >
                                <MenuItem value="PENDING">⏳ Pending</MenuItem>
                                <MenuItem value="CLOSED">✅ Closed</MenuItem>
                            </Select>
                        </FormControl>

                        <TextField
                            fullWidth
                            multiline
                            rows={2}
                            size="small"
                            label="Status Note *"
                            placeholder="Explain why you're changing the status..."
                            value={statusNote}
                            onChange={(e) => setStatusNote(e.target.value)}
                            helperText="This note will be shown in the activity timeline"
                            required
                        />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 2, pt: 0 }}>
                    <Button
                        onClick={() => setStatusDialogOpen(false)}
                        disabled={changingStatus}
                        size="small"
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleStatusChange}
                        disabled={!newStatus || !statusNote.trim() || changingStatus || newStatus === reminder?.status}
                        size="small"
                        sx={{ fontSize: '0.75rem' }}
                    >
                        {changingStatus ? 'Updating...' : 'Update Status'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <DeleteConfirmDialog
                open={deleteDialogOpen}
                loading={deleting}
                title="Delete Reminder"
                description={
                    <>
                        Are you sure you want to delete this reminder?
                        <br /><br />
                        <Alert severity="warning" sx={{ fontSize: "12px", py: "0px" }}>
                            This action cannot be undone. All associated data will be permanently removed.
                        </Alert>
                    </>
                }
                onCancel={() => setDeleteDialogOpen(false)}
                onConfirm={handleDelete}
            />
        </>
    );
};

export default ShowReminder;